import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import {
  FiSearch,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertTriangle,
  FiClock,
  FiActivity,
  FiAward,
  FiTarget,
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiLoader,
  FiBook,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import {
  searchStudentsApi,
  getStudentOverviewApi,
  getStudentSessionHistoryApi,
  endEnrollmentApi,
  StudentSearchResult,
  StudentOverviewData,
  SessionHistoryRow,
} from '../../config/api';
import { TrackAnalytics, GradeAnalytics } from '../../api/admin';
import { CQPM_TARGETS_BY_TRACK, ACCURACY_PASS_THRESHOLD } from '../../types/constants';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// ============================================================================
// ACCESS DENIED COMPONENT
// ============================================================================

const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// ============================================================================
// HEADER COMPONENT
// ============================================================================

const DashboardHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Student Overview
    </span>
  </h1>
);

// ============================================================================
// SEARCH WITH AUTOCOMPLETE COMPONENT
// ============================================================================

interface SearchWithAutocompleteProps {
  onSelectStudent: (email: string, name: string) => void;
  selectedStudentName: string | null;
  selectedStudentEmail: string | null;
  onClearSelection: () => void;
}

const SearchWithAutocomplete: React.FC<SearchWithAutocompleteProps> = ({
  onSelectStudent,
  selectedStudentName,
  selectedStudentEmail,
  onClearSelection,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<StudentSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const latestSearchQueryRef = useRef('');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const normalizedQuery = value.trim();
    latestSearchQueryRef.current = normalizedQuery;

    if (normalizedQuery.length < 2) {
      setSearchResults([]);
      setIsDropdownOpen(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setIsDropdownOpen(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const result = await searchStudentsApi(normalizedQuery);
        if (latestSearchQueryRef.current !== normalizedQuery) {
          return;
        }
        setSearchResults(result.users);
      } catch {
        if (latestSearchQueryRef.current !== normalizedQuery) {
          return;
        }
        setSearchResults([]);
      } finally {
        if (latestSearchQueryRef.current === normalizedQuery) {
          setIsSearching(false);
        }
      }
    }, 300);
  }, []);

  const handleSelectStudent = (student: StudentSearchResult) => {
    const name = `${student.givenName} ${student.familyName}`;
    onSelectStudent(student.email, name);
    setSearchQuery('');
    setIsDropdownOpen(false);
    setSearchResults([]);
  };

  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-4">
        <div className="relative" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Student by Name or Email
          </label>

          {selectedStudentName ? (
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{selectedStudentName}</p>
                <p className="text-sm text-gray-600">{selectedStudentEmail}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setIsDropdownOpen(true)}
                  placeholder="Type a name or email to search..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                />
                {isSearching && (
                  <FiLoader className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 animate-spin" />
                )}
              </div>

              {/* Dropdown */}
              {isDropdownOpen && !isSearching && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {searchResults.map((student) => (
                    <button
                      key={student.sourcedId}
                      onClick={() => handleSelectStudent(student)}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {student.givenName} {student.familyName}
                          </p>
                          <p className="text-sm text-gray-500">{student.email}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {isDropdownOpen && !isSearching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                  No students found matching "{searchQuery}"
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// STUDENT SUMMARY CARDS
// ============================================================================

interface StudentSummaryProps {
  student: StudentOverviewData;
}

const StudentSummary: React.FC<StudentSummaryProps> = ({ student }) => {
  const cqpmPercentage = student.targetCqpm > 0 ? Math.min((student.currentCqpm / student.targetCqpm) * 100, 100) : 0;
  const isAtGoal = student.currentCqpm >= student.targetCqpm;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* Current CQPM Card */}
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Current CQPM</p>
            <div className={`p-2 rounded-lg ${isAtGoal ? 'bg-green-100' : 'bg-blue-100'}`}>
              <FiTarget className={`w-5 h-5 ${isAtGoal ? 'text-green-600' : 'text-blue-600'}`} />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <p className={`text-3xl font-bold ${isAtGoal ? 'text-green-600' : 'text-blue-600'}`}>
              {student.currentCqpm}
            </p>
            <p className="text-sm text-gray-500 mb-1">/ {student.targetCqpm} goal</p>
          </div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${isAtGoal ? 'bg-green-500' : 'bg-blue-500'}`}
              style={{ width: `${cqpmPercentage}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Trend Card */}
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Trend</p>
            <div
              className={`p-2 rounded-lg ${
                student.trend === 'up'
                  ? 'bg-green-100'
                  : student.trend === 'down'
                  ? 'bg-red-100'
                  : 'bg-gray-100'
              }`}
            >
              {student.trend === 'up' ? (
                <FiTrendingUp className="w-5 h-5 text-green-600" />
              ) : student.trend === 'down' ? (
                <FiTrendingDown className="w-5 h-5 text-red-600" />
              ) : (
                <FiActivity className="w-5 h-5 text-gray-600" />
              )}
            </div>
          </div>
          <p
            className={`text-3xl font-bold ${
              student.trend === 'up'
                ? 'text-green-600'
                : student.trend === 'down'
                ? 'text-red-600'
                : 'text-gray-600'
            }`}
          >
            {student.trend === 'up' ? '+' : ''}
            {student.trendValue.toFixed(2)}
          </p>
          <p className="text-sm text-gray-500">CQPM/day</p>
        </CardContent>
      </Card>

      {/* Days on Track Card */}
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Days on Track</p>
            <div
              className={`p-2 rounded-lg ${student.daysOnCurrentTrack > 14 ? 'bg-yellow-100' : 'bg-blue-100'}`}
            >
              <FiClock
                className={`w-5 h-5 ${student.daysOnCurrentTrack > 14 ? 'text-yellow-600' : 'text-blue-600'}`}
              />
            </div>
          </div>
          <p
            className={`text-3xl font-bold ${
              student.daysOnCurrentTrack > 14 ? 'text-yellow-600' : 'text-blue-600'
            }`}
          >
            {student.daysOnCurrentTrack}
          </p>
          <p className="text-sm text-gray-500">days</p>
        </CardContent>
      </Card>

      {/* Alerts Card */}
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Active Alerts</p>
            <div
              className={`p-2 rounded-lg ${student.alerts.length > 0 ? 'bg-red-100' : 'bg-green-100'}`}
            >
              <FiAlertTriangle
                className={`w-5 h-5 ${student.alerts.length > 0 ? 'text-red-600' : 'text-green-600'}`}
              />
            </div>
          </div>
          <p
            className={`text-3xl font-bold ${
              student.alerts.length > 0 ? 'text-red-600' : 'text-green-600'
            }`}
          >
            {student.alerts.length}
          </p>
          <p className="text-sm text-gray-500">{student.alerts.length === 0 ? 'No issues' : 'Needs attention'}</p>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// CURRENT TRACK SECTION
// ============================================================================

interface CurrentTrackSectionProps {
  student: StudentOverviewData;
}

const CurrentTrackSection: React.FC<CurrentTrackSectionProps> = ({ student }) => {
  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Current Track</h2>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xl font-semibold text-gray-900">{student.currentTrack}</p>
              <p className="text-sm text-gray-600">{student.knowledgeGrade}</p>
            </div>

            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{student.currentCqpm}</p>
                <p className="text-xs text-gray-500">Current CQPM</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{student.targetCqpm}</p>
                <p className="text-xs text-gray-500">Target</p>
              </div>
              <div className="text-center">
                <p
                  className={`text-2xl font-bold ${
                    student.currentCqpm >= student.targetCqpm
                      ? 'text-green-600'
                      : 'text-orange-600'
                  }`}
                >
                  {student.targetCqpm - student.currentCqpm > 0
                    ? `-${student.targetCqpm - student.currentCqpm}`
                    : '+' + (student.currentCqpm - student.targetCqpm)}
                </p>
                <p className="text-xs text-gray-500">Gap</p>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts for current track */}
        {student.alerts.length > 0 && (
          <div className="mt-4 space-y-2">
            {student.alerts.map((alert, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  alert.type === 'CONSECUTIVE_FAILS' ? 'bg-red-50' : 'bg-yellow-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <FiAlertTriangle
                    className={`w-5 h-5 ${
                      alert.type === 'CONSECUTIVE_FAILS' ? 'text-red-600' : 'text-yellow-600'
                    }`}
                  />
                  <div>
                    <p
                      className={`font-medium ${
                        alert.type === 'CONSECUTIVE_FAILS' ? 'text-red-800' : 'text-yellow-800'
                      }`}
                    >
                      {alert.type === 'CONSECUTIVE_FAILS' ? 'Consecutive Fails' : 'Low CQPM Progress'}
                    </p>
                    <p className="text-sm text-gray-600">{alert.details}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500">{alert.triggeredDate}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// GRADE STATS SECTION
// ============================================================================

interface GradeStatsSectionProps {
  student: StudentOverviewData;
}

const GradeStatsSection: React.FC<GradeStatsSectionProps> = ({ student }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (student.gradeStats.length === 0) return null;

  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between mb-4"
        >
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiAward className="w-5 h-5 text-purple-600" />
            Time & XP by Grade Level
          </h2>
          {isExpanded ? (
            <FiChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <FiChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {isExpanded && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grade
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total XP
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg XP/Day
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Time
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Time/Day
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {student.gradeStats.map((grade, index) => (
                  <tr key={grade.grade} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {grade.grade}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {grade.totalXp}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                      {grade.avgXpPerDay.toFixed(1)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900">
                      {grade.totalTime} min
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                      {grade.avgTimePerDay} min
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          grade.tracksCompleted === grade.totalTracks
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {grade.tracksCompleted}/{grade.totalTracks} tracks
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// ENROLLMENTS SECTION
// ============================================================================

interface EnrollmentsSectionProps {
  student: StudentOverviewData;
  onRefresh: () => void;
}

const EnrollmentsSection: React.FC<EnrollmentsSectionProps> = ({ student, onRefresh }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showEnded, setShowEnded] = useState(false);
  const [endingEnrollmentId, setEndingEnrollmentId] = useState<string | null>(null);

  const { enrollments } = student;

  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const endedEnrollments = enrollments.filter(e => e.status === 'ended');

  const visibleEnrollments = showEnded ? enrollments : activeEnrollments;

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleEndEnrollment = async (enrollmentId: string, courseTitle: string, courseId: string) => {
    const confirmed = window.confirm(`Are you sure you want to end the enrollment in "${courseTitle}"?`);
    if (!confirmed) return;

    const oneRosterSourcedId = student.oneRosterSourcedId;
    if (!oneRosterSourcedId) {
      alert('Cannot end enrollment: student has no OneRoster ID.');
      return;
    }

    setEndingEnrollmentId(enrollmentId);
    try {
      await endEnrollmentApi(enrollmentId, courseId, oneRosterSourcedId);
      onRefresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to end enrollment');
    } finally {
      setEndingEnrollmentId(null);
    }
  };

  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between mb-4"
        >
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiBook className="w-5 h-5 text-blue-600" />
            Enrollments
          </h2>
          {isExpanded ? (
            <FiChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <FiChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {isExpanded && (
          <>
            {enrollments.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No enrollments found</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Course
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Begin Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          End Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {visibleEnrollments.map((enrollment, index) => (
                        <tr key={enrollment.enrollmentId || enrollment.courseId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {enrollment.courseTitle}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                enrollment.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {enrollment.status === 'active' ? 'Active' : 'Ended'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(enrollment.beginDate)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(enrollment.endDate)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {enrollment.status === 'active' && (
                              <button
                                onClick={() => handleEndEnrollment(enrollment.enrollmentId, enrollment.courseTitle, enrollment.courseId)}
                                disabled={endingEnrollmentId === enrollment.enrollmentId}
                                className="px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {endingEnrollmentId === enrollment.enrollmentId ? 'Ending...' : 'End'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {endedEnrollments.length > 0 && (
                  <div className="mt-3 flex items-center gap-2">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showEnded}
                        onChange={e => setShowEnded(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      Show ended enrollments
                      {!showEnded && (
                        <span className="text-gray-400">(+{endedEnrollments.length} ended)</span>
                      )}
                    </label>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// CQPM ANALYTICS SECTION (Charts + Track Stats + Grade Summaries)
// ============================================================================

interface TrackChartProps {
  track: TrackAnalytics;
}

const TrackChart: React.FC<TrackChartProps> = ({ track }) => {
  const dates = track.assessments.map(a => new Date(a.date).toLocaleDateString());
  const cqpmScores = track.assessments.map(a => a.cqpm);
  const accuracyScores = track.assessments.map(a => a.accuracy);

  const pointColors = accuracyScores.map(accuracy =>
    accuracy >= ACCURACY_PASS_THRESHOLD ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)'
  );
  const pointBorderColors = accuracyScores.map(accuracy =>
    accuracy >= ACCURACY_PASS_THRESHOLD ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)'
  );

  const trendlineData = track.assessments.map((_, index) => {
    if (index === 0) return track.trendline.minCQPM;
    if (index === track.assessments.length - 1) return track.trendline.maxCQPM;
    return null;
  });

  const goalCqpm = CQPM_TARGETS_BY_TRACK[track.trackId as keyof typeof CQPM_TARGETS_BY_TRACK] || 30;
  const goalLineData = track.assessments.map(() => goalCqpm);

  const chartData = {
    labels: dates,
    datasets: [
      {
        label: 'Assessment CQPM',
        data: cqpmScores,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: pointColors,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointBorderColors,
        tension: 0.1,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
      {
        label: 'Trendline',
        data: trendlineData,
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderDash: [5, 5],
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        spanGaps: true,
      },
      {
        label: 'Goal',
        data: goalLineData,
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        borderDash: [5, 5],
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      title: {
        display: true,
        text: track.trackName,
        font: { size: 16, weight: 'bold' },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (context.dataset.label === 'Assessment CQPM') {
              const accuracy = accuracyScores[context.dataIndex];
              return [
                `CQPM: ${Math.round(context.parsed.y)}`,
                `Accuracy: ${Math.round(accuracy)}%`
              ];
            }
            return `${context.dataset.label}: ${Math.round(context.parsed.y)} CQPM`;
          }
        }
      }
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'CQPM Score' } },
      x: { title: { display: true, text: 'Assessment Date' } },
    },
  };

  return (
    <div className="mb-6">
      <div style={{ height: '300px' }}>
        <Line data={chartData} options={options} />
      </div>
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Days Elapsed</p>
            <p className="font-semibold text-gray-900">{track.daysElapsed} days</p>
          </div>
          <div>
            <p className="text-gray-600">XP Spent</p>
            <p className="font-semibold text-gray-900">{track.xpSpent.toFixed(1)}</p>
          </div>
          <div>
            <p className="text-gray-600">Total Facts</p>
            <p className="font-semibold text-gray-900">{track.newFactsCount} facts</p>
          </div>
          <div>
            <p className="text-gray-600">Learning Rate</p>
            <p className="font-semibold text-gray-900">{track.learningRate.toFixed(3)} mins/fact/cqpm</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4 pt-4 border-t border-gray-200">
          <div>
            <p className="text-gray-600">Total Time</p>
            <p className="font-semibold text-gray-900">{track.totalTimeSpent.toFixed(1)} mins</p>
          </div>
          <div>
            <p className="text-gray-600">Active Time</p>
            <p className="font-semibold text-gray-900">{track.activeTimeSpent.toFixed(1)} mins</p>
          </div>
          <div>
            <p className="text-gray-600">Waste Time</p>
            <p className="font-semibold text-gray-900">{track.wasteTimeSpent.toFixed(1)} mins</p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface GradeSectionProps {
  grade: GradeAnalytics;
}

const GradeSection: React.FC<GradeSectionProps> = ({ grade }) => {
  const comparisonPercent = grade.gradeMetrics.benchmarkRate > 0
    ? ((grade.gradeMetrics.gradeLearningRate - grade.gradeMetrics.benchmarkRate) / grade.gradeMetrics.benchmarkRate * 100)
    : 0;

  const isFaster = comparisonPercent < 0;
  const comparisonText = isFaster
    ? `${Math.abs(comparisonPercent).toFixed(1)}% faster than benchmark`
    : `${comparisonPercent.toFixed(1)}% slower than benchmark`;

  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">{grade.gradeName}</h2>

      {grade.tracks.map((track) => (
        <TrackChart key={track.trackId} track={track} />
      ))}

      <Card className="shadow-lg border border-gray-100 bg-blue-50">
        <CardContent className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{grade.gradeName} Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Total XP</p>
              <p className="font-semibold text-gray-900">{grade.gradeMetrics.totalXpSpent.toFixed(1)} mins</p>
            </div>
            <div>
              <p className="text-gray-600">Learning Rate</p>
              <p className="font-semibold text-gray-900">
                {grade.gradeMetrics.gradeLearningRate.toFixed(3)} mins/fact/cqpm
              </p>
            </div>
            <div>
              <p className="text-gray-600">Benchmark</p>
              <p className="font-semibold text-gray-900">
                {grade.gradeMetrics.benchmarkRate.toFixed(3)} mins/fact/cqpm
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-4 pt-4 border-t border-gray-200">
            <div>
              <p className="text-gray-600">Total Time</p>
              <p className="font-semibold text-gray-900">{grade.gradeMetrics.totalTimeSpent.toFixed(1)} mins</p>
            </div>
            <div>
              <p className="text-gray-600">Active Time</p>
              <p className="font-semibold text-gray-900">{grade.gradeMetrics.totalActiveTimeSpent.toFixed(1)} mins</p>
            </div>
            <div>
              <p className="text-gray-600">Waste Time</p>
              <p className="font-semibold text-gray-900">{grade.gradeMetrics.totalWasteTimeSpent.toFixed(1)} mins</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-gray-600">Comparison</p>
            <p className={`font-semibold ${isFaster ? 'text-green-600' : 'text-orange-600'}`}>
              {comparisonText}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

interface CqpmAnalyticsSectionProps {
  student: StudentOverviewData;
}

const CqpmAnalyticsSection: React.FC<CqpmAnalyticsSectionProps> = ({ student }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!student.gradeData || student.gradeData.length === 0) return null;

  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between mb-4"
        >
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiActivity className="w-5 h-5 text-indigo-600" />
            CQPM Analytics
          </h2>
          {isExpanded ? (
            <FiChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <FiChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {isExpanded && (
          <div>
            {student.gradeData.map((grade) => (
              <GradeSection key={grade.grade} grade={grade} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// SESSION HISTORY SECTION
// ============================================================================

interface SessionHistorySectionProps {
  studentEmail: string;
}

const SessionHistorySection: React.FC<SessionHistorySectionProps> = ({ studentEmail }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SessionHistoryRow[] | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);
    setData(null);

    getStudentSessionHistoryApi(studentEmail)
      .then((result) => {
        if (!cancelled) {
          setData(result.rows);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load session history');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [studentEmail]);

  const getAccuracyClass = (accuracy: number | null): string => {
    if (accuracy === null) return 'text-gray-900';
    if (accuracy >= 90) return 'text-green-600 font-medium';
    if (accuracy >= 80) return 'text-gray-900';
    return 'text-red-600 font-medium';
  };

  return (
    <Card className="shadow-lg border border-gray-100 mb-6">
      <CardContent className="p-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between mb-4"
        >
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FiClock className="w-5 h-5 text-teal-600" />
            Session History
          </h2>
          {isExpanded ? (
            <FiChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <FiChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {isExpanded && (
          <>
            {isLoading && (
              <div className="py-8 text-center">
                <FiLoader className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-2" />
                <p className="text-gray-500 text-sm">Loading session history...</p>
              </div>
            )}

            {!isLoading && error && (
              <div className="py-4 text-center">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {!isLoading && !error && data && data.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Track
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total (min)
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Active (min)
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Waste (min)
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        XP
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        CQPM
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Accuracy
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row, index) => (
                      <tr key={`${row.date}-${row.trackId ?? 'none'}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {row.date}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {row.trackName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {row.totalTimeMinutes.toFixed(1)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {row.activeTimeMinutes.toFixed(1)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {row.wasteTimeMinutes.toFixed(1)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {row.xp.toFixed(1)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {row.assessmentCqpm !== null ? row.assessmentCqpm.toFixed(1) : '-'}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm ${getAccuracyClass(row.assessmentAccuracy)}`}>
                          {row.assessmentAccuracy !== null ? `${row.assessmentAccuracy.toFixed(0)}%` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isLoading && !error && data && data.length === 0 && (
              <div className="py-4 text-center">
                <p className="text-gray-500 text-sm">No session data found</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

export const UnifiedStudentDashboard: React.FC = () => {
  const studentNotLoggedInIndicator = 'AlphaNumbers profile has not been created yet';
  const { user: currentUser } = useAuth();
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [studentData, setStudentData] = useState<StudentOverviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNotLoggedIn, setHasNotLoggedIn] = useState(false);

  const isAdmin = currentUser?.role === 'admin';

  const handleSelectStudent = useCallback(async (email: string, name: string) => {
    setSelectedEmail(email);
    setSelectedName(name);
    setIsLoading(true);
    setError(null);
    setStudentData(null);
    setHasNotLoggedIn(false);

    try {
      const data = await getStudentOverviewApi(email);
      setStudentData(data);
    } catch (err) {
      if (err instanceof Error && err.message.includes(studentNotLoggedInIndicator)) {
        setHasNotLoggedIn(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load student data');
      }
    } finally {
      setIsLoading(false);
    }
  }, [studentNotLoggedInIndicator]);

  const handleClearSelection = useCallback(() => {
    setSelectedEmail(null);
    setSelectedName(null);
    setStudentData(null);
    setError(null);
    setHasNotLoggedIn(false);
  }, []);

  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <DashboardHeader />

        {/* Navigation */}
        <div className="flex mb-4">
          <Link
            to="/admin"
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            &larr; Back to Admin Dashboard
          </Link>
        </div>

        {/* Search */}
        <SearchWithAutocomplete
          onSelectStudent={handleSelectStudent}
          selectedStudentName={selectedName}
          selectedStudentEmail={selectedEmail}
          onClearSelection={handleClearSelection}
        />

        {/* Loading State */}
        {isLoading && (
          <Card className="shadow-lg border border-gray-100">
            <CardContent className="p-8 text-center">
              <FiLoader className="w-10 h-10 mx-auto text-blue-500 animate-spin mb-4" />
              <p className="text-gray-600">Loading student data...</p>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Card className="shadow-lg border border-red-200">
            <CardContent className="p-6 text-center">
              <FiAlertTriangle className="w-10 h-10 mx-auto text-red-500 mb-4" />
              <p className="text-red-700 font-medium">Failed to load student data</p>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </CardContent>
          </Card>
        )}

        {hasNotLoggedIn && !isLoading && (
          <Card className="shadow-lg border border-amber-200 bg-amber-50">
            <CardContent className="p-8 text-center">
              <FiSearch className="w-10 h-10 mx-auto text-amber-500 mb-4" />
              <p className="text-amber-900 font-medium">AlphaNumbers account not set up yet</p>
              <p className="text-amber-800 text-sm mt-1">
                This student was found in roster search, but their AlphaNumbers profile has not been created yet. This usually happens before a student logs in for the first time, so there is no activity to show yet.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Student Details */}
        {studentData && !isLoading && (
          <>
            <StudentSummary student={studentData} />
            <EnrollmentsSection student={studentData} onRefresh={() => handleSelectStudent(selectedEmail!, selectedName!)} />
            <CurrentTrackSection student={studentData} />
            <GradeStatsSection student={studentData} />
            <CqpmAnalyticsSection student={studentData} />
            <SessionHistorySection studentEmail={studentData.email} />
          </>
        )}

        {/* Empty state */}
        {!selectedEmail && !isLoading && (
          <Card className="shadow-lg border border-gray-100">
            <CardContent className="p-8 text-center">
              <div className="text-gray-400 mb-4">
                <FiSearch className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Search for a Student</h3>
              <p className="text-gray-600">
                Start typing a student's name or email in the search box above to see their overview.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UnifiedStudentDashboard;
